import { NotificationMessage } from '../api/models';
import { NotificationsQueryFilters, useNotificationsQuery } from '../api/repositories/use-notifications.query';
import { useWebSocketContext } from '../context/notifications-web-socket-context';
import { useIsInIframe } from '../hooks/use-is-in-Iframe';
import { HeaderComponent } from './header';
import { MessageItem } from './message-item';
import styles from './styles.module.scss';
import clsx from 'clsx';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

type Props = {
  onClose: VoidFunction;
};
export const NotificationMessages: FC<Props> = ({ onClose }) => {
  const isInIframe = useIsInIframe();
  const { subscribe, unsubscribe } = useWebSocketContext();
  const [totalMessages, setTotalMessages] = useState(0);
  const pageSize = 20;
  const [messages, setMessages] = useState<NotificationMessage[]>();
  const [showMessageType, setShowMassageType] = useState<'All' | 'Unread' | 'Read'>('All');

  const [pagingState, setPagingState] = useState<NotificationsQueryFilters>({
    page: 1,
    pageSize: pageSize
  });

  const { isFetching: isFetchingMessages } = useNotificationsQuery(pagingState, undefined, {
    onSuccess: (messageData) => {
      setTotalMessages(messageData?.count ?? 0);

      if (!messageData?.count) {
        setMessages([]);
        return;
      }

      const filteredItems = (messageData.items ?? []).filter((item) => item.sender?.id !== 0);

      setMessages((currentState) => {
        const newState = [...(currentState ?? []), ...filteredItems];
        return newState;
      });
    }
  });

  useEffect(() => {
    if (!subscribe || !unsubscribe) return;

    const handleUserNotification = (data: any) => {
      if (!data) return;

      // Фильтруем новые сообщения из WebSocket - исключаем те, у которых sender.id === 0
      if (data.sender?.id === 0) {
        return;
      }

      setMessages((currentState) => {
        const newState = [{ ...data }, ...(currentState ?? [])];
        return newState;
      });
    };

    subscribe('usernotification', handleUserNotification);

    return () => {
      unsubscribe('usernotification', handleUserNotification);
    };
  }, [subscribe, unsubscribe]);

  const scrollRef = useRef<HTMLElement | null>(null);

  const getIsReadFilter = (messageType?: 'All' | 'Unread' | 'Read') => {
    const currentType = messageType ?? showMessageType;
    return currentType === 'Read' ? true : currentType === 'Unread' ? false : undefined;
  };

  const handleScrollY = (container: HTMLElement) => {
    if (isFetchingMessages) return;
    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setPagingState((currentState) => {
        if (!totalMessages) return currentState;

        const countPages = Math.ceil(totalMessages / pageSize);

        if (currentState.page >= countPages) return currentState;

        const isRead = getIsReadFilter();

        return {
          page: currentState.page + 1,
          pageSize: currentState.pageSize,
          ...(isRead !== undefined && { isRead })
        };
      });
    }
  };

  const grouped = useMemo(() => {
    return messages
      ?.filter((x) => {
        if (showMessageType === 'Read') return x.isRead;
        if (showMessageType === 'Unread') return !x.isRead;
        return true; // All - показываем все
      })
      .filter((x) => x.sender?.id !== 0)
      .reduce<Record<string, NotificationMessage[]>>((acc, notification) => {
        const date = notification.createdAt?.slice(0, 10);
        if (!date) return acc;
        if (!acc[date]) acc[date] = [];
        acc[date].push(notification);
        return acc;
      }, {});
  }, [messages, showMessageType]);

  useEffect(() => {
    if (isInIframe) {
      const originalHtmlOverflow = document.documentElement.style.overflow;
      const originalBodyOverflow = document.body.style.overflow;
      const root = document.getElementById('root');

      if (root) {
        root.style.backgroundColor = '$fff';
      }

      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      return () => {
        document.documentElement.style.overflow = originalHtmlOverflow;
        document.body.style.overflow = originalBodyOverflow;
      };
    }
  }, [isInIframe]);

  const formatDateLabel = (dateStr: string): string => {
    const today = new Date();
    const target = new Date(dateStr);

    const isToday = today.toDateString() === target.toDateString();

    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const isYesterday = yesterday.toDateString() === target.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    return dateStr;
  };

  const getScrollHeight = (): string => {
    if (isInIframe) {
      return 'calc(100vh)';
    }

    return 'calc(100vh - 100px)';
  };

  return (
    <div className={clsx('container', 'p-0')} style={{ backgroundColor: isInIframe ? 'white' : undefined }}>
      <div className="row">
        <div className="col-12">
          <HeaderComponent
            showMessageType={showMessageType}
            setShowMassageType={(messageType) => {
              const isRead = getIsReadFilter(messageType);
              setPagingState({
                page: 1,
                pageSize: pageSize,
                ...(isRead !== undefined && { isRead })
              });

              setShowMassageType(messageType);
              setMessages([]);
            }}
            onClose={onClose}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <PerfectScrollbar
            containerRef={(ref) => {
              if (ref) scrollRef.current = ref;
            }}
            style={{
              height: getScrollHeight(),
              paddingRight: '20px',
              marginBottom: '10px',
              marginTop: '10px',
              width: '100%'
            }}
            onScrollY={handleScrollY}
          >
            {Object.entries(grouped ?? []).map(([date, items]) => (
              <div key={date} className={clsx(styles.dayContainer, 'mt-5', isInIframe ? styles.frame : null)}>
                <div className={clsx(styles.dateHeader)}>{formatDateLabel(date)}</div>

                {items.map((x, i) => {
                  return <MessageItem key={x.id} item={x} />;
                })}
              </div>
            ))}
          </PerfectScrollbar>
        </div>
      </div>
    </div>
  );
};
